import { POSITIONS } from '../../types';
import type { Position } from '../../types';

/**
 * §17.2 — the fifteen position chips, **laid out as the pitch's lines**.
 *
 * One component for both grids (*Normally plays*, single-select; *Can also play*, multi-select),
 * because they are the same fifteen targets in the same order and a second copy would be a second
 * place for the order to drift. `POSITIONS` is the order (§15.2: *"it is the order the position
 * grids render in, so a grid re-ordering is a visible edit there rather than a quiet one in a
 * component"*), and this component reads it rather than restating it.
 *
 * The rows are the coach's own reading of his own shorthand: goalkeeper alone, then the back line
 * and wing-backs, then the midfield spine and the wide midfielders, then the forwards.
 *
 * Every chip is ≥44 pt. Selection is **never hue-only** — `aria-pressed` plus a filled background
 * plus a weight change — and the *taken* state (a position already claimed as primary, offered in
 * the secondary grid) is `disabled` with its own treatment, so a position **cannot be its own
 * secondary by construction** rather than by a validation message after the fact.
 */

/** The pitch's lines, as index ranges into {@link POSITIONS}. Sums to fifteen. */
const LINES: readonly (readonly Position[])[] = [
  POSITIONS.slice(0, 1),
  POSITIONS.slice(1, 6),
  POSITIONS.slice(6, 11),
  POSITIONS.slice(11, 15),
];

export interface PositionGridProps {
  /** Probe id root — the two grids on one sheet must be separately addressable. */
  readonly testId: string;
  readonly ariaLabel: string;
  /** Positions currently chosen in THIS grid. */
  readonly selected: readonly Position[];
  /**
   * Positions this grid may not offer — the primary, in the secondary grid. Rendered visibly as
   * *taken* rather than hidden, so the fifteen never reflow and the user can see why.
   */
  readonly taken?: readonly Position[];
  readonly disabled?: boolean;
  readonly onToggle: (position: Position) => void;
}

export function PositionGrid({
  testId,
  ariaLabel,
  selected,
  taken = [],
  disabled = false,
  onToggle,
}: PositionGridProps) {
  const chosen = new Set(selected);
  const blocked = new Set(taken);

  return (
    <div
      className="myteam-grid"
      data-testid={testId}
      role="group"
      aria-label={ariaLabel}
      data-selected={selected.join(' ')}
    >
      {LINES.map((line, index) => (
        <div className="myteam-grid__line" key={index}>
          {line.map((position) => {
            const isTaken = blocked.has(position);
            return (
              <button
                key={position}
                type="button"
                className="myteam-chip"
                data-testid={`${testId}-${position}`}
                aria-pressed={chosen.has(position)}
                data-taken={isTaken ? 'true' : 'false'}
                disabled={disabled || isTaken}
                onClick={() => {
                  onToggle(position);
                }}
              >
                {position}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
